/**
 * Day 3: Fibonacci Forever
 * An infinite Fibonacci spiral with animated zoom
 */

import { BasePrompt } from './base.js';

export class Day03 extends BasePrompt {
  constructor(p5Instance, bridge) {
    super(p5Instance, bridge);
    
    this.phi = (1 + Math.sqrt(5)) / 2; // Golden ratio
    this.fibonacciSequence = this.generateFibonacci(20);
    this.scale = 84; // Base size for the first square (larger for tighter initial zoom)
    this.viewScale = 1;
    this.targetViewScale = 2;
    this.zoomStartScale = 1;
    this.zoomTransitionDuration = 200; // ms
    this.lastZoomUpdateTime = 0;
    this.viewCenter = { x: 0, y: 0 };
    this.targetCenter = { x: 0, y: 0 };
    this.centerStart = { x: 0, y: 0 };
    this.centerTransitionDuration = 200; // ms
    this.lastCenterUpdateTime = 0;
    this.historyLength = 10;
    this.maxWorldMagnitude = 1e6;
    
    // Animation state
    this.currentIndex = 0;
    this.rectProgress = 0;
    this.arcProgress = 0;
    
    // Timing (in milliseconds)
    this.rectDuration = 100;
    this.arcDuration = 100;
    this.pauseDuration = 1000;
    
    this.stateStartTime = 0;
    this.state = 'rect'; // 'rect', 'arc', 'pause'
    
    // Transform state - precompute all positions
    this.transforms = this.computeTransforms();

    // Audio interaction state
    this.audioReady = false;
    this.audioGroups = { noise: null, bass: null, lead: null };
    this.groupsCreated = false;
    this.noiseTriggerIndex = -1;
    this.arpTriggerIndex = -1;
    this.bassTriggerIndex = -1;
    this.centerDirectionPattern = ['up', 'up', 'down', 'down'];
    this.centerDirectionStep = 0;
    this.chordRoots = [60, 62, 64, 66]; // Major 7(9) stepping by whole tone
    this.nextNodeId = 5200;
    this.pendingTimeouts = [];
  }

  generateFibonacci(count) {
    const fib = [1, 1];
    for (let i = 2; i < count; i++) {
      fib.push(fib[i - 1] + fib[i - 2]);
    }
    return fib;
  }

  /**
   * Compute cumulative transforms for each square in the spiral
   * Uses a consistent recursive transformation:
   * 1. Move to end of previous arc
   * 2. Rotate 90 degrees
   * 3. Adjust origin to align start of new arc with end of previous
   */
  computeTransforms() {
    const transforms = [];
    const f = this.fibonacciSequence;
    
    let x = 0;
    let y = 0;
    let rotation = 0;
    
    // Start at origin
    transforms.push({ x, y, rotation });
    
    for (let i = 0; i < f.length - 1; i++) {
      const currentSize = f[i] * this.scale;
      const nextSize = f[i + 1] * this.scale;
      
      // 1. Translate to end of current arc (0, size) in local coords
      // x += -sin(rot) * size
      // y += cos(rot) * size
      x += -Math.sin(rotation) * currentSize;
      y += Math.cos(rotation) * currentSize;
      
      // 2. Rotate 90 degrees
      rotation += Math.PI / 2;
      
      // 3. Translate back by nextSize in X (in new local coords) to align
      // x += cos(rot) * (-nextSize)
      // y += sin(rot) * (-nextSize)
      x += Math.cos(rotation) * (-nextSize);
      y += Math.sin(rotation) * (-nextSize);
      
      transforms.push({ x, y, rotation });
    }
    
    return transforms;
  }

  getBounds(startIndex, maxIndex) {
    const start = Math.max(0, startIndex || 0);
    const end = Math.max(start, maxIndex || start);
    const bounds = {
      minX: Infinity,
      maxX: -Infinity,
      minY: Infinity,
      maxY: -Infinity,
      width: 0,
      height: 0
    };

    for (let i = start; i <= end; i++) {
      const size = this.fibonacciSequence[i] * this.scale;
      const transform = this.transforms[i];
      const cosR = Math.cos(transform.rotation);
      const sinR = Math.sin(transform.rotation);

      const corners = [
        { x: 0, y: 0 },
        { x: size, y: 0 },
        { x: size, y: size },
        { x: 0, y: size }
      ];

      for (const corner of corners) {
        const worldX = transform.x + cosR * corner.x - sinR * corner.y;
        const worldY = transform.y + sinR * corner.x + cosR * corner.y;

        bounds.minX = Math.min(bounds.minX, worldX);
        bounds.maxX = Math.max(bounds.maxX, worldX);
        bounds.minY = Math.min(bounds.minY, worldY);
        bounds.maxY = Math.max(bounds.maxY, worldY);
      }
    }

    bounds.width = bounds.maxX - bounds.minX;
    bounds.height = bounds.maxY - bounds.minY;

    return bounds;
  }

  setup() {
    this.p5.background(0);
    this.p5.rectMode(this.p5.CORNER);
    
    const sequencer = this.bridge.sequencer;
    sequencer.setBPM(110);
    
    this.stateStartTime = this.p5.millis();
  }

  setupAudio() {
    if (!this.audio) {
      return;
    }

    this.ensureAudioGroups();
    this.audioReady = true;
    this.noiseTriggerIndex = -1;
    this.arpTriggerIndex = -1;
    this.bassTriggerIndex = -1;

    if (this.state === 'rect') {
      this.onEnterRectState(false);
    }
  }

  ensureAudioGroups() {
    if (!this.audio || this.groupsCreated) {
      return;
    }

    const baseGroup = 4300;
    this.audio.send('/g_new', baseGroup, 0, 0);
    this.audio.send('/g_new', baseGroup + 1, 3, baseGroup);
    this.audio.send('/g_new', baseGroup + 2, 3, baseGroup + 1);
    this.audioGroups = {
      noise: baseGroup,
      bass: baseGroup + 1,
      lead: baseGroup + 2,
    };
    this.groupsCreated = true;
  }

  onEnterRectState(isNewIndex) {
    if (!this.audioReady) {
      return;
    }

    this.triggerRectNoise(this.currentIndex);
    if (isNewIndex) {
      this.triggerCenterBass(this.currentIndex);
    }
  }

  onEnterArcState() {
    if (!this.audioReady) {
      return;
    }
    this.triggerLeadArp(this.currentIndex);
  }

  triggerRectNoise(index) {
    if (!this.audioReady || !this.audio) {
      return;
    }
    if (this.noiseTriggerIndex === index) {
      return;
    }

    this.noiseTriggerIndex = index;
    const releaseSeconds = (this.rectDuration / 1000) * 0.2;
    const freqBand = 300 + (index % 5) * 220;
    const amp = Math.min(0.35, 0.18 + Math.min(index, 12) * 0.01);

    this.audio.synth('sonic-pi-chipnoise', {
      amp,
      attack: 0.005,
      decay: releaseSeconds * 0.05,
      sustain: 0.15,
      release: 0.10,
      freq_band: freqBand,
      pan: this.p5.map(Math.sin(index * 0.6), -1, 1, -0.4, 0.4)
    }, { target: this.audioGroups.noise ?? 0 });
  }

  triggerCenterBass(index) {
    if (!this.audioReady || !this.audio) {
      return;
    }
    if (this.bassTriggerIndex === index) {
      return;
    }

    this.bassTriggerIndex = index;
    const direction = this.centerDirectionPattern[this.centerDirectionStep % this.centerDirectionPattern.length];
    this.centerDirectionStep++;
    const baseNote = 36 + (index % 5);
    const slide = direction === 'up' ? 3 : -3;
    const targetNote = this.p5.constrain(baseNote + slide, 28, 52);

    const nodeId = this.spawnSynth('sonic-pi-chipbass', {
      note: baseNote,
      note_slide: 0.25,
      amp: 0.35,
      attack: 0.005,
      decay: 0.18,
      sustain: 0.35,
      release: 0.6,
      pan: this.p5.map(Math.cos(index * 0.5), -1, 1, -0.4, 0.4)
    }, this.audioGroups.bass ?? 0);

    if (nodeId) {
      this.scheduleTimeout(() => {
        if (this.audio) {
          this.audio.send('/n_set', nodeId, 'note', targetNote);
        }
      }, 40);
    }
  }

  triggerLeadArp(index) {
    if (!this.audioReady || !this.audio) {
      return;
    }
    if (this.arpTriggerIndex === index) {
      return;
    }

    this.arpTriggerIndex = index;
    const chord = this.getChordForIndex(index);
    const stepMs = 20;

    chord.forEach((note, idx) => {
      this.scheduleTimeout(() => {
        if (!this.audio) {
          return;
        }
        this.audio.synth('sonic-pi-chiplead', {
          note,
          amp: 0.12,
          attack: 0.01,
          decay: 0.05,
          sustain: 0.25,
          release: 0.05,
          width: 0.4 + idx * 0.08,
          pan: this.p5.map(idx, 0, chord.length - 1, -0.5, 0.5)
        }, { target: this.audioGroups.lead ?? 0 });
      }, stepMs * idx);
    });
  }

  getChordForIndex(index) {
    const chordIdx = index % this.chordRoots.length;
    const root = this.chordRoots[chordIdx];
    return [root, root + 4, root + 7, root + 11, root + 14];
  }

  spawnSynth(name, params = {}, target = 0, action = 0) {
    if (!this.audio) {
      return null;
    }
    const nodeId = this.nextNodeId++;
    const args = this.flattenParams(params);
    this.audio.send('/s_new', name, nodeId, action, target ?? 0, ...args);
    return nodeId;
  }

  flattenParams(params) {
    const flattened = [];
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (typeof value === 'number' && Number.isNaN(value)) {
        return;
      }
      flattened.push(key, value);
    });
    return flattened;
  }

  scheduleTimeout(callback, delay) {
    const timerId = setTimeout(() => {
      callback();
      this.pendingTimeouts = this.pendingTimeouts.filter((id) => id !== timerId);
    }, delay);
    this.pendingTimeouts.push(timerId);
    return timerId;
  }

  draw() {
    const now = this.p5.millis();
    const elapsed = now - this.stateStartTime;

    this.updateAnimationState(elapsed);

    this.p5.background(0);
    
    const centerX = this.p5.width / 2;
    const centerY = this.p5.height / 2;

    const maxRenderableIndex = Math.min(this.currentIndex, this.fibonacciSequence.length - 1);
    const startIndex = Math.max(0, maxRenderableIndex - this.historyLength);
    this.extendSequenceTo(this.currentIndex + this.historyLength + 2);
    const bounds = this.getBounds(startIndex, maxRenderableIndex);
    const width = Math.max(1, bounds.width);
    const height = Math.max(1, bounds.height);
    const padding = 0.8;
    const scaleX = (this.p5.width - padding) / width * padding;
    const scaleY = (this.p5.height - padding) / height * padding;
    this.targetViewScale = Math.min(scaleX, scaleY);
    this.viewScale = this.computeSmoothZoom(this.targetViewScale);
    const targetCenter = {
      x: bounds.minX + bounds.width / 2,
      y: bounds.minY + bounds.height / 2
    };
    const currentCenter = this.computeSmoothCenter(targetCenter);
    
    this.p5.push();
    this.p5.translate(centerX, centerY);
    this.p5.scale(this.viewScale);
    this.p5.translate(-currentCenter.x, -currentCenter.y);
    
    // Draw all completed rectangles and arcs
    for (let i = startIndex; i < this.currentIndex; i++) {
      this.drawSquare(i, 1.0);
      this.drawArc(i, 1.0);
    }

    const shouldDrawCurrentRect = this.state === 'rect' || this.state === 'arc' || this.state === 'pause';
    const shouldDrawCurrentArc = this.state === 'arc' || this.state === 'pause';
    const rectProgress = this.state === 'pause' ? 1 : this.rectProgress;
    const arcProgress = this.state === 'pause' ? 1 : this.arcProgress;

    // Draw current square with progress
    if (shouldDrawCurrentRect) {
      this.drawSquare(this.currentIndex, rectProgress);
    }

    // Draw current arc with progress
    if (shouldDrawCurrentArc) {
      this.drawArc(this.currentIndex, arcProgress);
    }

    this.p5.pop();
  }

  updateAnimationState(elapsed) {
    switch (this.state) {
      case 'rect':
        this.rectProgress = Math.min(elapsed / this.rectDuration, 1.0);
        if (elapsed >= this.rectDuration) {
          this.state = 'arc';
          this.stateStartTime = this.p5.millis();
          this.rectProgress = 1;
          this.arcProgress = 0;
          this.onEnterArcState();
        }
        break;

      case 'arc':
        this.arcProgress = Math.min(elapsed / this.arcDuration, 1.0);
        if (elapsed >= this.arcDuration) {
          this.state = 'pause';
          this.stateStartTime = this.p5.millis();
          this.arcProgress = 1;
        }
        break;

      case 'pause':
        if (elapsed >= this.pauseDuration) {
          this.currentIndex++;
          this.state = 'rect';
          this.stateStartTime = this.p5.millis();
          this.rectProgress = 0;
          this.arcProgress = 0;
          this.zoomStartScale = this.viewScale;
          this.lastZoomUpdateTime = this.p5.millis();
          this.extendSequenceTo(this.currentIndex + this.historyLength + 2);
          this.onEnterRectState(true);
        }
        break;
    }
  }

  /**
   * Draw a single square in the Fibonacci spiral
   * Uses the precomputed transform for position
   */
  drawSquare(index, progress) {
    const size = this.fibonacciSequence[index] * this.scale;
    const transform = this.transforms[index];
    
    const hue = (index * 30) % 360;
    this.p5.noFill();
    this.p5.stroke(hue, 70, 80);
    const strokeWidth = Math.max(1, 2 / this.viewScale);
    this.p5.strokeWeight(strokeWidth);

    this.p5.push();
    this.p5.translate(transform.x, transform.y);
    this.p5.rotate(transform.rotation);
    
    // Draw square
    // We animate from Right to Left because the arc starts at (size, 0)
    const animatedWidth = size * progress;
    this.p5.rect(size - animatedWidth, 0, animatedWidth, size);
    
    // Draw index number
    this.p5.fill(0);
    this.p5.noStroke();
    this.p5.textAlign(this.p5.CENTER, this.p5.CENTER);
    this.p5.textSize(Math.max(8, size / 8));
    this.p5.text(index, size / 2, size / 2);
    
    this.p5.pop();
  }

  computeSmoothZoom(newTarget) {
    if (this.targetViewScale !== newTarget) {
      this.zoomStartScale = this.viewScale;
      this.lastZoomUpdateTime = this.p5.millis();
    }
    this.targetViewScale = newTarget;
    const elapsed = this.p5.millis() - this.lastZoomUpdateTime;
    const t = Math.min(1, elapsed / this.zoomTransitionDuration);
    const eased = t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2; // ease in-out quad
    return this.p5.lerp(this.zoomStartScale, this.targetViewScale, eased);
  }

  computeSmoothCenter(newTarget) {
    if (
      this.targetCenter.x !== newTarget.x ||
      this.targetCenter.y !== newTarget.y
    ) {
      this.centerStart = { ...this.viewCenter };
      this.targetCenter = { ...newTarget };
      this.lastCenterUpdateTime = this.p5.millis();
    }

    const elapsed = this.p5.millis() - this.lastCenterUpdateTime;
    const t = Math.min(1, elapsed / this.centerTransitionDuration);
    const eased = t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;

    this.viewCenter = {
      x: this.p5.lerp(this.centerStart.x, this.targetCenter.x, eased),
      y: this.p5.lerp(this.centerStart.y, this.targetCenter.y, eased)
    };

    return this.viewCenter;
  }

  extendSequenceTo(targetIndex) {
    while (this.fibonacciSequence.length <= targetIndex + 2) {
      const len = this.fibonacciSequence.length;
      const next = this.fibonacciSequence[len - 1] + this.fibonacciSequence[len - 2];
      this.fibonacciSequence.push(next);
    }

    while (this.transforms.length <= targetIndex) {
      const lastIndex = this.transforms.length - 1;
      const prevTransform = this.transforms[lastIndex];
      const prevRotation = prevTransform.rotation;
      const prevSize = this.fibonacciSequence[lastIndex] * this.scale;
      const nextIndex = lastIndex + 1;
      const nextSize = this.fibonacciSequence[nextIndex] * this.scale;
      let x = prevTransform.x - Math.sin(prevRotation) * prevSize;
      let y = prevTransform.y + Math.cos(prevRotation) * prevSize;
      const rotation = prevRotation + Math.PI / 2;
      x += Math.cos(rotation) * (-nextSize);
      y += Math.sin(rotation) * (-nextSize);
      this.transforms.push({ x, y, rotation });
    }

    this.normalizeIfNeeded();
  }

  normalizeIfNeeded() {
    const lastTransform = this.transforms[this.transforms.length - 1];
    const maxCoord = Math.max(
      Math.abs(lastTransform.x),
      Math.abs(lastTransform.y)
    );
    const lastSize = this.fibonacciSequence[this.fibonacciSequence.length - 1] * this.scale;
    if (maxCoord < this.maxWorldMagnitude && lastSize < this.maxWorldMagnitude) {
      return;
    }

    const factor = Math.pow(this.phi, 6);

    for (let i = 0; i < this.transforms.length; i++) {
      this.transforms[i].x /= factor;
      this.transforms[i].y /= factor;
    }

    this.scale /= factor;

    this.viewCenter.x /= factor;
    this.viewCenter.y /= factor;
    this.targetCenter.x /= factor;
    this.targetCenter.y /= factor;
    this.centerStart.x /= factor;
    this.centerStart.y /= factor;

    this.viewScale *= factor;
    this.zoomStartScale *= factor;
    this.targetViewScale *= factor;
  }

  /**
   * Draw the arc for a square
   * Arc always connects the same relative corners
   */
  drawArc(index, progress) {
    const size = this.fibonacciSequence[index] * this.scale;
    const transform = this.transforms[index];
    
    const hue = (index * 30) % 360;
    this.p5.stroke(hue, 60, 100);
    const strokeWidth = Math.max(1, 2 / this.viewScale);
    this.p5.strokeWeight(strokeWidth);
    this.p5.noFill();

    this.p5.push();
    this.p5.translate(transform.x, transform.y);
    this.p5.rotate(transform.rotation);
    
    // Arc is always the bottom-right quadrant of a circle centered at (0,0)
    // Starts at (size, 0) [0 radians]
    // Ends at (0, size) [PI/2 radians]
    const startAngle = 0;
    const endAngle = Math.PI / 2;
    const animEnd = startAngle + (endAngle - startAngle) * progress;
    
    this.p5.arc(0, 0, size * 2, size * 2, startAngle, animEnd);
    
    this.p5.pop();
  }

  cleanup() {
    if (this.pendingTimeouts.length) {
      this.pendingTimeouts.forEach((id) => clearTimeout(id));
      this.pendingTimeouts = [];
    }

    if (this.audio && this.groupsCreated) {
      Object.values(this.audioGroups).forEach((groupId) => {
        this.audio.send('/g_freeAll', groupId);
        this.audio.send('/n_free', groupId);
      });
      this.groupsCreated = false;
    }

    this.audioReady = false;

    if (this.bridge && this.bridge.sequencer) {
      this.bridge.sequencer.stop();
      this.bridge.sequencer.clear();
    }
  }
}
